import { useCallback, useEffect, useState } from "react";
import { DEFAULT_COLUMNS } from "../pages/Console/screeningFields";

const STORAGE_KEY = "v1.console.screeningColumns";

/**
 * Which screening_data columns the Screening tab shows.
 *
 * A display preference, so it lives in localStorage, not on the server — losing it costs nothing
 * and a per-user column on the backend would be a migration and an endpoint for the privilege of
 * remembering three checkboxes. Global rather than per-job: a column that a given job never
 * captured is hidden by the table anyway (see Console/index.js), so a stale selection degrades to
 * "shows fewer columns", not to "shows dead columns".
 */
const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(raw);
    // An empty array is a real choice (everything toggled off) — only a non-array is corrupt.
    if (!Array.isArray(parsed)) return DEFAULT_COLUMNS;
    return parsed.filter((key) => typeof key === "string");
  } catch {
    return DEFAULT_COLUMNS;
  }
};

const useScreeningColumns = () => {
  const [columns, setColumns] = useState(read);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
    } catch {
      // Private mode or a full quota. The choice just doesn't survive the reload — not worth
      // failing a render over.
    }
  }, [columns]);

  const toggle = useCallback((key) => {
    setColumns((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  }, []);

  return { columns, toggle };
};

export default useScreeningColumns;
